import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, getToken, msg, num, time, HUB_BASE } from './hubApi';
import { useApp } from './HubContext';

function Player({ stream }) {
  if (!stream.embed) return <div className="player empty"><p>This leader hasn’t added a stream link yet.</p></div>;
  if (!stream.live) return <div className="player empty"><p>Offline. Picks and chat from the last stream are on the right.</p></div>;
  const src = stream.embed.replace('{HOST}', window.location.hostname);
  return (
    <div className="player">
      <iframe src={src} title="Live stream" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin" sandbox="allow-scripts allow-same-origin allow-popups allow-presentation" />
    </div>
  );
}

const PROVIDERS = { youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick' };

function PickCard({ c, isFollower, isOwn, paid, onCopy, copied }) {
  const p = c.pick;
  if (p.masked) return (
    <div className="pickcard masked"><b>New pick</b> · jumps {time(p.startTime)}<br /><span className="fine">{paid ? 'Buyers' : 'Followers'} see this pick before the race. Open the leader’s profile to get access.</span></div>
  );
  const started = new Date(p.startTime) <= new Date();
  return (
    <div className="pickcard">
      <div className="row between"><b>PICK</b><span className="fine">{time(c.createdAt)}</span></div>
      <div className="pickline">{p.venue} R{p.raceNo} · {p.runnerNumber}. {p.runnerName}</div>
      <div className="fine">{p.units != null ? p.units + 'u · ' : ''}{p.side === 'LAY' ? 'Lay ' : ''}{p.betType} · tip price {num(p.decisionPrice)} · jumps {time(p.startTime)}</div>
      {isOwn ? <div className="note">Your pick. Followers on Mimic already have it.</div>
        : isFollower ? <div className="note">Already in your account through Mimic.</div>
        : started ? <div className="note">Race has started.</div>
        : copied ? <div className="note">{copied}</div>
        : <button className="sm" onClick={() => onCopy(p.tipId)}>Copy this pick</button>}
    </div>
  );
}

export default function Watch() {
  const { handle } = useParams();
  const { user } = useApp();
  const [w, setW] = useState(null);
  const [comments, setComments] = useState([]);
  const [viewers, setViewers] = useState(0);
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState({});
  const listRef = useRef(null);
  const stick = useRef(true);

  const load = () => api('/watch/' + handle).then(d => { setW(d); setComments(d.comments); setViewers(d.viewers); }).catch(e => setErr(msg(e)));
  useEffect(() => {
    load();
    // Live updates over Server-Sent Events, read with fetch so the login header goes with it.
    const ctrl = new AbortController();
    const onEvent = e => {
      if (e.type === 'comment') setComments(cs => cs.some(c => c.id === e.comment.id) ? cs : [...cs.slice(-299), e.comment]);
      if (e.type === 'delete') setComments(cs => cs.map(c => c.id === e.id ? { ...c, deleted: true, text: null } : c));
      if (e.type === 'viewers') setViewers(e.viewers);
      if (e.type === 'live') load();
    };
    (async () => {
      while (!ctrl.signal.aborted) {
        try {
          const res = await fetch(`${HUB_BASE}/watch/${handle}/events`, { headers: getToken() ? { Authorization: 'Bearer ' + getToken() } : {}, signal: ctrl.signal });
          const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = '';
          for (;;) {
            const { value, done } = await reader.read(); if (done) break;
            buf += dec.decode(value, { stream: true });
            let i; while ((i = buf.indexOf('\n\n')) >= 0) {
              const block = buf.slice(0, i); buf = buf.slice(i + 2);
              const data = block.split('\n').filter(l => l.startsWith('data: ')).map(l => l.slice(6)).join('');
              if (data) { try { onEvent(JSON.parse(data)); } catch {} }
            }
          }
        } catch { if (ctrl.signal.aborted) return; }
        await new Promise(r => setTimeout(r, 3000));
      }
    })();
    return () => ctrl.abort();
  }, [handle, user?._id]);
  useEffect(() => { if (stick.current && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [comments]);

  const send = async e => {
    e.preventDefault(); setErr('');
    try { await api(`/watch/${handle}/comments`, { method: 'POST', body: { text } }); setText(''); }
    catch (e) { setErr(msg(e)); }
  };
  const del = id => api(`/watch/${handle}/comments/${id}`, { method: 'DELETE' }).catch(e => setErr(msg(e)));
  const mute = uid => api(`/watch/${handle}/mute`, { method: 'POST', body: { userId: uid, minutes: 60 } }).then(() => setErr('User muted for 1 hour.')).catch(e => setErr(msg(e)));
  const report = id => api(`/watch/${handle}/report`, { method: 'POST', body: { commentId: id, reason: 'viewer report' } }).then(() => setErr('Reported. Thanks.'));
  const copy = async tipId => {
    try { const r = await api(`/picks/${tipId}/copy`, { method: 'POST' }); setCopied(c => ({ ...c, [tipId]: `Added to “${r.source.name}” (${r.source.mode.toLowerCase()} mode). Change it under Sources & limits.` })); }
    catch (e) { setCopied(c => ({ ...c, [tipId]: msg(e) })); }
  };

  if (!w) return <p>{err || 'Loading…'}</p>;
  return (
    <>
      <div className="row between watchhead">
        <div>
          <h1>{w.stream.title || w.leader.displayName}</h1>
          <span className="fine"><Link to={'/hub/leaders/' + w.leader.handle}>{w.leader.displayName}</Link> · {w.stream.live ? <span className="pill live">LIVE</span> : 'offline'} · {viewers} watching</span>
        </div>
        {!w.isFollower && user?._id !== w.leader.id && <Link to={'/hub/leaders/' + w.leader.handle}><button className="ghost sm">{w.paid ? 'Get access' : 'Follow / mimic'}</button></Link>}
      </div>
      <div className="watch">
        <Player stream={w.stream} />
        <section className="chat card">
          <div className="chatlist" ref={listRef} onScroll={e => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40; }}>
            {!comments.length && <p className="fine">No messages yet.</p>}
            {comments.map(c => c.kind === 'PICK'
              ? <PickCard key={c.id} c={c} isFollower={w.isFollower} isOwn={user?._id === w.leader.id} paid={w.paid} onCopy={copy} copied={copied[c.pick.tipId]} />
              : c.kind === 'SYSTEM' ? <div key={c.id} className="sysmsg">{c.text}</div>
              : (
                <div key={c.id} className="msg">
                  <b className={c.isLeader ? 'lead' : ''}>{c.displayName}</b> {c.deleted ? <i className="muted">message removed</i> : c.text}
                  {!c.deleted && user && (
                    <span className="msgtools">
                      {w.isMod && <><button className="link" onClick={() => del(c.id)}>delete</button>{!c.isLeader && <button className="link" onClick={() => mute(c.userId)}>mute</button>}</>}
                      {!w.isMod && c.userId !== user._id && <button className="link" onClick={() => report(c.id)}>report</button>}
                    </span>)}
                </div>))}
          </div>
          {w.canComment ? (
            <form className="chatform" onSubmit={send}>
              <input value={text} maxLength={300} onChange={e => setText(e.target.value)} placeholder="Say something (no links)" />
              <button disabled={!text.trim()}>Send</button>
            </form>) : <p className="fine"><Link to="/">Log in</Link> to chat.</p>}
          {err && <p className="note">{err}</p>}
        </section>
      </div>
      <p className="fine">Streams are the leader’s own commentary, hosted on {PROVIDERS[w.stream.provider] || 'YouTube, Twitch or Kick'}. Betbuilder doesn’t show race vision. Copied picks go into your own account under your own limits. Gamble responsibly.</p>
    </>
  );
}
